import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Package, MapPin, Phone, User, Calendar, Hash, 
  Banknote, Printer, Edit, CheckCircle2, Clock, 
  Truck, AlertCircle, ArrowRight, FileText
} from 'lucide-react';
import { api } from '@/lib/api';

const TimelineItem = ({ icon: Icon, title, date, runner, reason, notes, status, isLast }) => {
  const isActive = status === 'active';
  const isCompleted = status === 'completed';
  
  return (
    <div className="flex gap-4 relative">
      {!isLast && (
        <div className={`absolute top-8 right-[19px] w-0.5 h-full ${isCompleted ? 'bg-orange-500' : 'bg-gray-200'}`} />
      )}
      <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        isCompleted ? 'bg-orange-100 text-orange-600' : 
        isActive ? 'bg-orange-100 text-orange-600 ring-4 ring-orange-50' : 
        'bg-gray-100 text-gray-400'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 pb-8">
        <h4 className={`font-bold ${isActive ? 'text-orange-700' : isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
          {title}
        </h4>
        {date && <p className="text-xs text-gray-500 mt-1 font-mono">{date}</p>}
        
        {/* عرض تفاصيل المندوب والسبب والملاحظات لكل محاولة */}
        <div className="mt-2 space-y-1 text-xs text-gray-600">
          {runner && (
            <div className="flex items-center gap-1">
              <Truck className="w-3.5 h-3.5 text-gray-400" />
              <span>المندوب: <strong className="text-gray-800">{runner}</strong></span>
            </div>
          )}
          {reason && (
            <div className="bg-orange-50 text-orange-700 p-2 rounded-lg border border-orange-100 mt-1">
              <span className="font-semibold">السبب:</span> {reason}
            </div>
          )}
          {notes && (
            <div className="text-gray-500 bg-gray-50 p-1.5 rounded mt-1">
              {notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ShipmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [trackingHistory, setTrackingHistory] = useState([]);

  // إخفاء التحديثات المتتالية المتطابقة مع الاحتفاظ بتغيرات الحالة الفعلية
  const visibleTrackingHistory = trackingHistory.filter((item: any, index: number, list: any[]) => {
    if (index === 0) return true;
    const currentStatus = item.statusAr || item.statusNameAr || item.status || item.statusName || '';
    const previousStatus = list[index - 1].statusAr || list[index - 1].statusNameAr || list[index - 1].status || list[index - 1].statusName || '';
    return currentStatus !== previousStatus;
  });  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShipment = async () => {
      try {
        const res = await api.get(`/api/shipments/${id}`);
        if (res.data?.success !== false) {
          const payload = res.data?.data ?? res.data;
          const item = payload?.shipment ?? payload;
          const normalized = {
            ...item,
            senderName: item?.senderName ?? item?.shipperName ?? item?.senderCustomer?.name ?? item?.createdByUser?.fullName ?? '',
            senderPhone: item?.senderPhone ?? item?.shipperPhone ?? item?.senderCustomer?.phone ?? item?.createdByUser?.phone ?? '',
            receiverName: item?.receiverName ?? item?.consigneeName ?? item?.recipientName ?? '',
            receiverPhone: item?.receiverPhone ?? item?.consigneePhone ?? item?.recipientPhone ?? '',
            receiverAddress: item?.receiverAddress ?? item?.address ?? '',
            receiverCity: item?.receiverCity ?? item?.city ?? ''
          };
          setShipment(normalized);
          const history = normalized?.trackingHistory ?? normalized?.history ?? [];
          setTrackingHistory(Array.isArray(history) ? history : []);
        }
      } catch (error) {
        console.error('Error fetching shipment:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchShipment();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900">الشحنة غير موجودة</h3>
        <button onClick={() => navigate('/shipments')} className="mt-4 text-orange-600 hover:underline">
          العودة إلى قائمة الشحنات
        </button>
      </div>
    );
  }

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/shipments')} className="p-2 hover:bg-gray-200 rounded-full transition">
              <ArrowRight className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                تفاصيل الشحنة
                <span className="text-sm font-mono bg-orange-100 text-orange-700 px-3 py-1 rounded-full border border-orange-200">
                  {shipment.trackingNumber || shipment.awbNumber}
                </span>
              </h1>
              <p className="text-gray-500 mt-1">آخر تحديث: {shipment.createdAt ? formatDate(shipment.createdAt) : ''}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate(`/shipments/${shipment.id}/print`)} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition text-gray-700">
              <Printer className="w-4 h-4" />
              طباعة / PDF
            </button>
            <button onClick={() => navigate(`/shipments/${id}/edit`)} className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition shadow-sm">
              <Edit className="w-4 h-4" />
              تعديل الشحنة
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Timeline & Status */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                سجل رحلة الشحنة (Shipment History)
              </h3>
              <div className="pr-2">
                {visibleTrackingHistory.length > 0 ? (
                  visibleTrackingHistory.map((item: any, index: number) => (
                    <TimelineItem
                      key={item.id || index}
                      icon={Package}
                      title={item.statusAr || item.statusNameAr || item.status || item.statusName || item.description || 'تحديث الشحنة'}
                      date={item.createdAt ? formatDate(item.createdAt) : null}
                      runner={item.runnerName || item.delegateName}
                      reason={item.reason}
                      notes={item.notes}
                      status={index === 0 ? 'active' : 'completed'}
                      isLast={index === visibleTrackingHistory.length - 1}
                    />
                  ))
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">
                    لا يوجد سجل تتبع للشحنة حتى الآن
                  </div>
                )}
              </div>
            </div>

            {/* Financial Summary Card */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-orange-600" />
                الملخص المالي
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">قيمة التحصيل (COD):</span>
                  <span className="font-bold text-orange-600 text-lg">{shipment.cod || shipment.codAmount || 0} ج.م</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Details Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Shipment Info */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-600" />
                معلومات الشحنة
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">رقم التتبع</p>
                  <p className="font-mono font-bold text-gray-900">{shipment.trackingNumber || shipment.awbNumber}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">المندوب الحالي</p>
                  <p className="font-bold text-gray-900">{shipment.runnerName || shipment.delegateName || 'غير محدد'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">الحالة الحالية</p>
                  <p className="font-bold text-orange-600">{shipment.statusNameAr || shipment.statusAr || shipment.statusName || shipment.status || 'غير محدد'}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Sender Card */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  بيانات المرسل (Shipper)
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-xs text-gray-500 block">الاسم</span>
                    <span className="font-semibold text-gray-900">{shipment.senderName || shipment.shipperName || 'غير محدد'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">الهاتف</span>
                    <span className="font-semibold text-gray-900 font-mono" dir="ltr">{shipment.senderPhone || shipment.shipperPhone || 'غير محدد'}</span>
                  </div>
                </div>
              </div>

              {/* Receiver Card */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-green-600" />
                  بيانات المستلم (Consignee)
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-xs text-gray-500 block">الاسم</span>
                    <span className="font-semibold text-gray-900">{shipment.receiverName || shipment.consigneeName || 'غير محدد'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">الهاتف</span>
                    <span className="font-semibold text-gray-900 font-mono" dir="ltr">{shipment.receiverPhone || shipment.consigneePhone || 'غير محدد'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">العنوان</span>
                    <span className="font-semibold text-gray-900">{shipment.receiverCity || shipment.city || ''} - {shipment.receiverAddress || shipment.address || 'غير محدد'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}